import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { ReviewCompanionshipType } from 'api/review'

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  margin-top: ${rem(16)};
  color: ${props => props.theme.palette.brand.primary.normal};
`

function toggleSelectedSet<T>(selectedSet: Set<T>, value: T) {
  const nextSelectedSet = new Set([...selectedSet.values()])

  if (nextSelectedSet.has(value)) {
    nextSelectedSet.delete(value)
  } else {
    nextSelectedSet.add(value)
  }

  return nextSelectedSet
}

type Option = {
  label: string
  value: string
}

function generateOptionItems(options: Array<Option>, localStates: Set<string>) {
  return options.map((item) => ({
    label: item.label,
    value: item.value,
    checked: localStates.has(item.value),
  }))
}

export interface UserReviewFiltersModalResult {
  ratings: Array<string>
  travellers: Array<ReviewCompanionshipType>
  months: Array<string>
}

interface LocalFilters {
  ratings?: Array<string>
  travellers?: Array<ReviewCompanionshipType>
  months?: Array<string>
}

interface Props {
  existingFilters?: LocalFilters
  isLoading?: boolean
}

function UserReviewFiltersModal(props: Props) {
  const {
    existingFilters,
    isLoading,
  } = props

  const intl = useIntl()
  const { resolve } = useModalElementContext<UserReviewFiltersModalResult>()

  const availableFilters = useMemo(() => ({
    ratings: [
      {
        label: intl.formatMessage({
          id: 'EP8tHNo9SwvbUS4wNLs-',
          defaultMessage: 'Exceptional: 9+',
        }),
        value: 'exceptional',
      },
      {
        label: intl.formatMessage({
          id: 'NZRDQ1Xx8skaZOcCtvYK',
          defaultMessage: 'Good: 7.0 - 8.9',
        }),
        value: 'good',
      },
      {
        label: intl.formatMessage({
          id: 'YBj6u3nxE9Y3aOqxjwXg',
          defaultMessage: 'Okay: 5.0 - 6.9',
        }),
        value: 'okay',
      },
      {
        label: intl.formatMessage({
          id: 'uTJDH00zPrRjjG9DPxjW',
          defaultMessage: 'Poor: 3.0 - 4.9',
        }),
        value: 'poor',
      },
      {
        label: intl.formatMessage({
          id: 'S_g2R02qg8zDVfeeilkJ',
          defaultMessage: 'Very poor: 1.0 - 2.9',
        }),
        value: 'very-poor',
      },
    ],
    travellers: [
      {
        label: intl.formatMessage({
          id: 'E5cwMe625uIB6nStYIhe',
          defaultMessage: 'Solo travellers',
        }),
        value: 'alone',
      },
      {
        label: intl.formatMessage({
          id: 'LaA0PgRiBlfdYL43-IMC',
          defaultMessage: 'Couples',
        }),
        value: 'partner',
      },
      {
        label: intl.formatMessage({
          id: '9AGC-od7JX-ZJFP_zvdw',
          defaultMessage: 'Families',
        }),
        value: 'family',
      },
      {
        label: intl.formatMessage({
          id: '29kovsTnLZQ1ZPChoZLx',
          defaultMessage: 'Friends',
        }),
        value: 'friends',
      },
      {
        label: intl.formatMessage({
          id: 'BlyO5zqOIz4X5uHCFxn5',
          defaultMessage: 'Business travellers',
        }),
        value: 'work',
      },
    ],
    months: [
      {
        label: intl.formatMessage({
          id: 'eLRUQ09YibIA8SoBapaL',
          defaultMessage: 'Dec - Feb',
        }),
        value: '12,1,2',
      },
      {
        label: intl.formatMessage({
          id: 'jeURiM4DrBwWkUhRO6fi',
          defaultMessage: 'Mar - May',
        }),
        value: '3,4,5',
      },
      {
        label: intl.formatMessage({
          id: 'RBiAcsMb4QMkAb4ulF6i',
          defaultMessage: 'Jun - Aug',
        }),
        value: '6,7,8',
      },
      {
        label: intl.formatMessage({
          id: 'iLLRyJtxsKRXcMyHyc3I',
          defaultMessage: 'Sep - Nov',
        }),
        value: '9,10,11',
      },
    ],
  }), [intl])
  const [localRatings, setLocalRatings] = useState<Set<string>>(
    existingFilters?.ratings ? new Set(existingFilters.ratings) : new Set(),
  )
  const [localCompanionship, setLocalCompanionship] = useState<Set<ReviewCompanionshipType>>(
    existingFilters?.travellers ? new Set(existingFilters.travellers) : new Set(),
  )
  const [localMonths, setLocalMonths] = useState<Set<string>>(
    existingFilters?.months ? new Set(existingFilters.months) : new Set(),
  )

  const onFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    resolve({
      ratings: [...localRatings],
      travellers: [...localCompanionship],
      months: [...localMonths],
    })
  }

  const onRatingsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextRatings = toggleSelectedSet(localRatings, e.target.value)
    setLocalRatings(nextRatings)
  }, [localRatings])

  const onCompanionshipChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextCompanionship = toggleSelectedSet(localCompanionship, e.target.value as ReviewCompanionshipType)
    setLocalCompanionship(nextCompanionship)
  }, [localCompanionship])

  const onMonthsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextMonths = toggleSelectedSet(localMonths, e.target.value)
    setLocalMonths(nextMonths)
  }, [localMonths])

  const ratingsItems = useMemo(
    () => generateOptionItems(availableFilters.ratings, localRatings),
    [availableFilters.ratings, localRatings],
  )

  const companionshipItems = useMemo(
    () => generateOptionItems(availableFilters.travellers, localCompanionship),
    [availableFilters.travellers, localCompanionship],
  )

  const monthsItems = useMemo(
    () => generateOptionItems(availableFilters.months, localMonths),
    [availableFilters.months, localMonths],
  )

  const onRatingsReset = useCallback(() => {
    setLocalRatings(new Set())
  }, [])

  const onCompanionshipReset = useCallback(() => {
    setLocalCompanionship(new Set())
  }, [])

  const onMonthsReset = useCallback(() => {
    setLocalMonths(new Set())
  }, [])

  const onFormReset = useCallback(() => {
    onRatingsReset()
    onCompanionshipReset()
    onMonthsReset()
  }, [onRatingsReset, onCompanionshipReset, onMonthsReset])

  return (
    <ModalBase mode="drawer">
      <form onSubmit={onFilterSubmit}>
        <ModalHeader title={intl.formatMessage({ id: '-dRnGOUj6NFQgYYbayq2', defaultMessage: 'Filters' })} />
        <ModalBody>
          <ModalContent>
            {isLoading && (
              <CSSBreakpoint min="tablet">
                <ColourfulLoadingAnimation size={8} />
              </CSSBreakpoint>
            )}
            <FilterPanelFilterGroup
              title={intl.formatMessage({ id: 'QmTGf32FTcNe2tlPEPp5', defaultMessage: 'Ratings' })}
              onReset={onRatingsReset}
              canReset={localRatings.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="ratings"
                items={ratingsItems}
                onChange={onRatingsChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
            <FilterPanelFilterGroup
              title={intl.formatMessage({ id: 'VQ42mwntZCt9LR6WI0Qf', defaultMessage: 'Travellers' })}
              onReset={onCompanionshipReset}
              canReset={localCompanionship.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="travellers"
                items={companionshipItems}
                onChange={onCompanionshipChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
            <FilterPanelFilterGroup
              title={intl.formatMessage({ id: '9gRZA43YEtoW7B4WCUWr', defaultMessage: 'Months' })}
              onReset={onMonthsReset}
              canReset={localMonths.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="months"
                items={monthsItems}
                onChange={onMonthsChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({ id: 'Ms4tSbFRa_hNZRkX8fy8', defaultMessage: 'View reviews' }),
            type: 'submit',
            'data-testid': 'view-reviews-filter-button',
          }}
          secondaryActionProps={{
            children: intl.formatMessage({ id: '1DRQCOWv4r1lCUXkPjw5', defaultMessage: 'Reset all' }),
            onClick: onFormReset,
          }}
        />
      </form>
    </ModalBase>
  )
}

export default UserReviewFiltersModal
